import React, { useState, useEffect, useRef } from 'react';
import { useOperatorProfileStore } from '../../stateManagers/stores/operatorProfileStore';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';

// ─── Design tokens ────────────────────────────────────────────────────────
const C = {
  navy:        '#0e225d',
  navyMid:     '#1a3370',
  navyHover:   'rgba(14,34,93,0.06)',
  gold:        '#fbb21f',
  goldDim:     'rgba(251,178,31,0.14)',
  goldBorder:  'rgba(251,178,31,0.35)',
  white:       '#ffffff',
  border:      '#e5e7eb',
  muted:       '#9ca3af',
  sub:         '#6b7280',
  body:        '#1f2937',
  bg:          '#f7f8fa',
  green:       '#16a34a',
  greenDim:    'rgba(22,163,74,0.12)',
  greenBorder: 'rgba(22,163,74,0.3)',
  red:         '#dc2626',
  redDim:      'rgba(220,38,38,0.08)',
  redBorder:   'rgba(220,38,38,0.25)',
};

// ─── Types ────────────────────────────────────────────────────────────────
interface Option {
  option_text: string;
  is_correct:  boolean;
}

interface Question {
  question_text: string;
  marks:         number;
  options:       Option[];
}

interface Batch {
  batch_id:    string;
  batch_name:  string;
  batch_time:  string;
  is_active:   boolean;
  start_month: string;
  start_year:  number;
  end_month:   string;
  end_year:    number;
}

interface FormState {
  title:            string;
  schedule_date:    string;
  schedule_time:    string;
  duration_minutes: number;
  batch_id:         string;
  questions:        Question[];
}

const blankOption   = (): Option    => ({ option_text: '', is_correct: false });
const blankQuestion = (): Question  => ({
  question_text: '',
  marks:         1,
  options:       [blankOption(), blankOption(), blankOption(), blankOption()],
});

// ─── Sub-components ───────────────────────────────────────────────────────
const Field: React.FC<{
  label:     string;
  required?: boolean;
  error?:    string;
  children:  React.ReactNode;
  style?:    React.CSSProperties;
}> = ({ label, required, error, children, style }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', ...style }}>
    <label style={{ fontSize: '0.78rem', fontWeight: '700', color: C.navy, letterSpacing: '0.02em' }}>
      {label}
      {required && <span style={{ color: C.red, marginLeft: '3px' }}>*</span>}
    </label>
    {children}
    {error && (
      <span style={{ fontSize: '0.72rem', color: C.red, display: 'flex', alignItems: 'center', gap: '4px' }}>
        <i className="pi pi-exclamation-circle" style={{ fontSize: '0.7rem' }} /> {error}
      </span>
    )}
  </div>
);

const inputStyle = (hasError?: boolean): React.CSSProperties => ({
  padding:      '9px 12px',
  border:       `1.5px solid ${hasError ? C.redBorder : C.border}`,
  borderRadius: '9px',
  fontSize:     '0.88rem',
  color:        C.body,
  background:   C.white,
  outline:      'none',
  width:        '100%',
  boxSizing:    'border-box',
  fontFamily:   'inherit',
  transition:   'border-color 0.15s',
});

// ─── Main Component ───────────────────────────────────────────────────────
const CreateClassTest: React.FC = () => {
  const { profile, fetchProfile } = useOperatorProfileStore();

  const [form, setForm] = useState<FormState>({
    title:            '',
    schedule_date:    '',
    schedule_time:    '',
    duration_minutes: 30,
    batch_id:         '',
    questions:        [blankQuestion()],
  });

  const [batches,      setBatches]      = useState<Batch[]>([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [errors,       setErrors]       = useState<Record<string, string>>({});
  const [submitting,   setSubmitting]   = useState(false);
  const [submitError,  setSubmitError]  = useState<string | null>(null);
  const [submitted,    setSubmitted]    = useState(false);
  const [activeQ,      setActiveQ]      = useState<number>(0);

  const questionRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (!profile) fetchProfile();
  }, []);

  useEffect(() => {
    if (profile?.institute_id && profile?.branch_id) {
      fetchBatches();
    }
  }, [profile]);

  const fetchBatches = async () => {
    setBatchLoading(true);
    try {
      const res = await apiClient.get<{ success: boolean; data: Batch[] }>(
        '/v1/batches?limit=100&status=active'
      );
      if (res.data.success) setBatches(res.data.data);
    } catch (e) {
      console.error('Failed to fetch batches', e);
    } finally {
      setBatchLoading(false);
    }
  };

  // ── Field helpers ──────────────────────────────────────────────────────
  const setTop = (key: keyof Omit<FormState, 'questions' | 'duration_minutes'>, val: string) => {
    setForm(prev => ({ ...prev, [key]: val }));
    setErrors(prev => { const n = { ...prev }; delete n[key]; return n; });
  };

  const setDuration = (val: string) => {
    const num = parseInt(val, 10);
    setForm(prev => ({ ...prev, duration_minutes: isNaN(num) ? 30 : Math.min(360, Math.max(1, num)) }));
    setErrors(prev => { const n = { ...prev }; delete n['duration_minutes']; return n; });
  };

  const setQuestionText = (qi: number, val: string) => {
    setForm(prev => {
      const qs = [...prev.questions];
      qs[qi] = { ...qs[qi], question_text: val };
      return { ...prev, questions: qs };
    });
    setErrors(prev => { const n = { ...prev }; delete n[`q${qi}_text`]; return n; });
  };

  const setQuestionMarks = (qi: number, val: string) => {
    const num = parseFloat(val);
    setForm(prev => {
      const qs = [...prev.questions];
      qs[qi] = { ...qs[qi], marks: isNaN(num) ? 1 : Math.max(0.5, num) };
      return { ...prev, questions: qs };
    });
  };

  const setOptionText = (qi: number, oi: number, val: string) => {
    setForm(prev => {
      const qs = [...prev.questions];
      const opts = [...qs[qi].options];
      opts[oi] = { ...opts[oi], option_text: val };
      qs[qi] = { ...qs[qi], options: opts };
      return { ...prev, questions: qs };
    });
    setErrors(prev => { const n = { ...prev }; delete n[`q${qi}_opt${oi}`]; delete n[`q${qi}_correct`]; return n; });
  };

  const setOptionCorrect = (qi: number, oi: number) => {
    setForm(prev => {
      const qs = [...prev.questions];
      const opts = qs[qi].options.map((o, i) => ({ ...o, is_correct: i === oi }));
      qs[qi] = { ...qs[qi], options: opts };
      return { ...prev, questions: qs };
    });
    setErrors(prev => { const n = { ...prev }; delete n[`q${qi}_correct`]; return n; });
  };

  const addOption = (qi: number) => {
    setForm(prev => {
      const qs = [...prev.questions];
      qs[qi] = { ...qs[qi], options: [...qs[qi].options, blankOption()] };
      return { ...prev, questions: qs };
    });
  };

  const removeOption = (qi: number, oi: number) => {
    setForm(prev => {
      const qs = [...prev.questions];
      const opts = qs[qi].options.filter((_, i) => i !== oi);
      const hasCorrect = opts.some(o => o.is_correct);
      if (!hasCorrect && opts.length > 0) opts[0] = { ...opts[0], is_correct: false };
      qs[qi] = { ...qs[qi], options: opts };
      return { ...prev, questions: qs };
    });
  };

  const addQuestion = () => {
    setForm(prev => ({ ...prev, questions: [...prev.questions, blankQuestion()] }));
    const next = form.questions.length;
    setActiveQ(next);
    setTimeout(() => {
      questionRefs.current[next]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const removeQuestion = (qi: number) => {
    if (form.questions.length === 1) return;
    setForm(prev => ({ ...prev, questions: prev.questions.filter((_, i) => i !== qi) }));
    setActiveQ(prev => Math.max(0, prev >= qi ? prev - 1 : prev));
  };

  const duplicateQuestion = (qi: number) => {
    setForm(prev => {
      const qs = [...prev.questions];
      const copy: Question = JSON.parse(JSON.stringify(qs[qi]));
      copy.question_text = copy.question_text ? `${copy.question_text} (copy)` : '';
      qs.splice(qi + 1, 0, copy);
      return { ...prev, questions: qs };
    });
    setActiveQ(qi + 1);
  };

  // ── Validation ─────────────────────────────────────────────────────────
  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!form.title.trim())         e['title']         = 'Title is required';
    if (!form.schedule_date.trim()) e['schedule_date'] = 'Date is required';
    if (!form.schedule_time.trim()) e['schedule_time'] = 'Time is required';
    if (!form.batch_id)             e['batch_id']      = 'Batch is required';
    if (form.duration_minutes < 1 || form.duration_minutes > 360) {
      e['duration_minutes'] = 'Duration must be between 1 and 360 minutes';
    }

    form.questions.forEach((q, qi) => {
      if (!q.question_text.trim()) e[`q${qi}_text`] = 'Question text is required';
      const nonEmptyOpts = q.options.filter(o => o.option_text.trim());
      if (nonEmptyOpts.length < 2) e[`q${qi}_opts`] = 'At least 2 options required';
      q.options.forEach((o, oi) => {
        if (!o.option_text.trim()) e[`q${qi}_opt${oi}`] = 'Option text is required';
      });
      if (!q.options.some(o => o.is_correct)) e[`q${qi}_correct`] = 'Mark one correct answer';
    });

    setErrors(e);

    if (Object.keys(e).length > 0) {
      const firstQErr = Object.keys(e).find(k => k.startsWith('q'));
      if (firstQErr) {
        const qi = parseInt(firstQErr.replace(/^q(\d+).*/, '$1'));
        setActiveQ(qi);
        setTimeout(() => {
          questionRefs.current[qi]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 50);
      }
    }

    return Object.keys(e).length === 0;
  };

  // ── Submit ─────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validate()) return;
    if (!profile)    return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      const payload = {
        institute_id:     profile.institute_id,
        branch_id:        profile.branch_id,
        batch_id:         form.batch_id,
        title:            form.title.trim(),
        subject:          form.title.trim(), // mirrors title since subject field removed
        schedule_date:    form.schedule_date,
        schedule_time:    form.schedule_time,
        duration_minutes: form.duration_minutes,
        status:           'scheduled',
        questions:        form.questions.map(q => ({
          question_text: q.question_text.trim(),
          marks:         q.marks,
          options:       q.options.map(o => ({
            option_text: o.option_text.trim(),
            is_correct:  o.is_correct,
          })),
        })),
      };

      await apiClient.post('/v1/class-tests', payload, {
        headers: { 'Content-Type': 'application/json' },
      });
      setSubmitted(true);
    } catch (err: any) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Failed to create class test';
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Success screen ─────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div style={{ background: C.bg, minHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' }}>
        <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '18px', padding: '40px 36px', maxWidth: '420px', width: '100%', textAlign: 'center', boxShadow: '0 2px 16px rgba(14,34,93,0.08)' }}>
          <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: C.greenDim, border: `1.5px solid ${C.greenBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <i className="pi pi-check-circle" style={{ fontSize: '1.6rem', color: C.green }} />
          </div>
          <div style={{ fontSize: '1.05rem', fontWeight: '800', color: C.navy, marginBottom: '8px' }}>Class Test Created</div>
          <div style={{ fontSize: '0.85rem', color: C.sub, lineHeight: 1.6, marginBottom: '28px' }}>
            <strong style={{ color: C.body }}>{form.title}</strong> has been saved with {form.questions.length} question{form.questions.length !== 1 ? 's' : ''} · {form.duration_minutes} min.
          </div>
          <button
            onClick={() => {
              setForm({ title: '', schedule_date: '', schedule_time: '', duration_minutes: 30, batch_id: '', questions: [blankQuestion()] });
              setActiveQ(0);
              setSubmitted(false);
            }}
            style={{ padding: '10px 24px', background: C.navy, border: 'none', borderRadius: '9px', color: C.white, fontSize: '0.85rem', fontWeight: '700', cursor: 'pointer' }}
          >
            Create Another Test
          </button>
        </div>
      </div>
    );
  }

  // ── Sidebar dot styles ─────────────────────────────────────────────────
  const qDotStyle = (qi: number): React.CSSProperties => {
    const isCur     = qi === activeQ;
    const hasErrors = Object.keys(errors).some(k => k.startsWith(`q${qi}`));
    const filled    = form.questions[qi].question_text.trim().length > 0;
    if (isCur)     return { background: C.navy,     color: C.white,   borderColor: C.navy };
    if (hasErrors) return { background: C.redDim,   color: C.red,     borderColor: C.redBorder };
    if (filled)    return { background: C.greenDim, color: '#166534', borderColor: '#86efac' };
    return           { background: C.white, color: C.sub, borderColor: C.border };
  };

  const totalMarks = form.questions.reduce((s, q) => s + q.marks, 0);
  const OPTION_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];

  const selectedBatch = batches.find(b => b.batch_id === form.batch_id);

  // Duration display helper
  const fmtDuration = (mins: number): string => {
    if (mins < 60) return `${mins} min`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m > 0 ? `${h}h ${m}min` : `${h}h`;
  };

  return (
    <>
      <style>{`
        .cct-input:focus  { border-color: ${C.navy} !important; }
        .cct-input:hover  { border-color: ${C.gold} !important; }
        .cct-opt:focus    { border-color: ${C.navy} !important; }
        .cct-opt:hover    { border-color: ${C.gold} !important; }
        .cct-qdot:hover   { opacity: 0.8; }
        .cct-remove:hover { background: ${C.redDim} !important; border-color: ${C.redBorder} !important; color: ${C.red} !important; }
        .cct-ghost:hover  { border-color: ${C.navy} !important; color: ${C.navy} !important; }
        .cct-radio        { appearance: none; -webkit-appearance: none; width: 16px; height: 16px; border-radius: 50%; border: 1.5px solid ${C.border}; cursor: pointer; flex-shrink: 0; transition: all 0.15s; background: ${C.white}; margin: 0; }
        .cct-radio:checked { background: ${C.gold}; border-color: ${C.gold}; box-shadow: inset 0 0 0 3px ${C.white}; }
        .cct-radio:hover   { border-color: ${C.gold}; }
        .cct-dur::-webkit-inner-spin-button, .cct-dur::-webkit-outer-spin-button { opacity: 1; }
        select.cct-input   { appearance: none; -webkit-appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 10px center; padding-right: 30px; }
        @keyframes slideIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .cct-qcard { animation: slideIn 0.2s ease-out; }
      `}</style>

      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: C.bg }}>

        {/* ── TOP BAR ─────────────────────────────────────────────────── */}
        <div style={{ background: C.white, borderBottom: `1px solid ${C.border}`, padding: '0 20px', height: '58px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, gap: '12px' }}>
          <div>
            <div style={{ fontWeight: '700', fontSize: '0.92rem', color: C.navy }}>New Class Test</div>
            <div style={{ fontSize: '0.75rem', color: C.muted, marginTop: '1px' }}>
              {profile?.branch_name ?? '—'} &nbsp;·&nbsp; {form.questions.length} question{form.questions.length !== 1 ? 's' : ''} &nbsp;·&nbsp; {totalMarks} marks &nbsp;·&nbsp; {fmtDuration(form.duration_minutes)}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {submitError && (
              <span style={{ fontSize: '0.75rem', color: C.red, maxWidth: '220px', textAlign: 'right', lineHeight: 1.4 }}>
                <i className="pi pi-exclamation-triangle" style={{ fontSize: '0.72rem' }} /> {submitError}
              </span>
            )}
            <button
              onClick={handleSubmit}
              disabled={submitting}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 20px', background: submitting ? C.muted : C.navy, border: 'none', borderRadius: '9px', color: C.white, fontSize: '0.83rem', fontWeight: '700', cursor: submitting ? 'not-allowed' : 'pointer', boxShadow: '0 2px 8px rgba(14,34,93,0.25)', transition: 'all 0.2s' }}
              onMouseEnter={e => { if (!submitting) e.currentTarget.style.background = C.navyMid; }}
              onMouseLeave={e => { if (!submitting) e.currentTarget.style.background = C.navy; }}
            >
              {submitting
                ? <><i className="pi pi-spin pi-spinner" style={{ fontSize: '0.85rem' }} /> Saving…</>
                : <><i className="pi pi-check-circle"   style={{ fontSize: '0.85rem' }} /> Save Test</>}
            </button>
          </div>
        </div>

        {/* ── BODY ────────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

          {/* Main scroll area */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>

            {/* ── Test Details card ─────────────────────────────────── */}
            <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '14px', padding: '22px 24px', boxShadow: '0 1px 4px rgba(14,34,93,0.05)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '18px' }}>
                <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: C.navyHover, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="pi pi-file-edit" style={{ fontSize: '0.85rem', color: C.navy }} />
                </div>
                <span style={{ fontWeight: '700', fontSize: '0.88rem', color: C.navy }}>Test Details</span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>

                {/* Title — full width */}
                <Field label="Title" required error={errors['title']} style={{ gridColumn: '1 / -1' }}>
                  <input
                    className="cct-input"
                    style={inputStyle(!!errors['title'])}
                    placeholder="e.g. Mathematics — Chapter 3 Quiz"
                    value={form.title}
                    onChange={e => setTop('title', e.target.value)}
                  />
                </Field>

                {/* Batch — full width */}
                <Field label="Batch" required error={errors['batch_id']} style={{ gridColumn: '1 / -1' }}>
                  <select
                    className="cct-input"
                    style={inputStyle(!!errors['batch_id'])}
                    value={form.batch_id}
                    onChange={e => setTop('batch_id', e.target.value)}
                    disabled={batchLoading}
                  >
                    <option value="">
                      {batchLoading ? 'Loading batches…' : '— Select a batch —'}
                    </option>
                    {batches.map(b => (
                      <option key={b.batch_id} value={b.batch_id}>
                        {b.batch_name} · {b.batch_time} ({b.start_month} {b.start_year} – {b.end_month} {b.end_year})
                      </option>
                    ))}
                  </select>
                  {selectedBatch && (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginTop: '4px', padding: '3px 10px', background: C.navyHover, border: `1px solid rgba(14,34,93,0.15)`, borderRadius: '20px', fontSize: '0.72rem', color: C.navy, fontWeight: '600' }}>
                      <i className="pi pi-users" style={{ fontSize: '0.68rem' }} />
                      {selectedBatch.batch_name}
                    </div>
                  )}
                </Field>

                {/* Date */}
                <Field label="Date" required error={errors['schedule_date']}>
                  <input
                    className="cct-input"
                    type="date"
                    style={inputStyle(!!errors['schedule_date'])}
                    value={form.schedule_date}
                    onChange={e => setTop('schedule_date', e.target.value)}
                  />
                </Field>

                {/* Time */}
                <Field label="Time" required error={errors['schedule_time']}>
                  <input
                    className="cct-input"
                    type="time"
                    style={inputStyle(!!errors['schedule_time'])}
                    value={form.schedule_time}
                    onChange={e => setTop('schedule_time', e.target.value)}
                  />
                </Field>

                {/* Duration — full width */}
                <Field label="Duration (minutes)" required error={errors['duration_minutes']} style={{ gridColumn: '1 / -1' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ position: 'relative', flex: 1 }}>
                      <i className="pi pi-clock" style={{ position: 'absolute', left: '11px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.85rem', color: C.muted, pointerEvents: 'none' }} />
                      <input
                        className="cct-input cct-dur"
                        type="number"
                        min={1}
                        max={360}
                        step={5}
                        style={{ ...inputStyle(!!errors['duration_minutes']), paddingLeft: '34px' }}
                        value={form.duration_minutes}
                        onChange={e => setDuration(e.target.value)}
                      />
                    </div>
                    {/* Quick-pick buttons */}
                    <div style={{ display: 'flex', gap: '5px', flexShrink: 0 }}>
                      {[15, 30, 45, 60, 90].map(d => (
                        <button
                          key={d}
                          type="button"
                          onClick={() => setDuration(String(d))}
                          style={{
                            padding: '7px 10px',
                            border: `1.5px solid ${form.duration_minutes === d ? C.navy : C.border}`,
                            borderRadius: '8px',
                            background: form.duration_minutes === d ? C.navy : C.white,
                            color: form.duration_minutes === d ? C.white : C.sub,
                            fontSize: '0.75rem',
                            fontWeight: '700',
                            cursor: 'pointer',
                            transition: 'all 0.15s',
                            fontFamily: 'inherit',
                          }}
                        >
                          {d < 60 ? `${d}m` : `${d / 60}h`}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div style={{ fontSize: '0.72rem', color: C.muted, marginTop: '4px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <i className="pi pi-info-circle" style={{ fontSize: '0.68rem' }} />
                    Students will have {fmtDuration(form.duration_minutes)} to complete this test. Timer starts when they open it.
                  </div>
                </Field>

              </div>
            </div>

            {/* ── Questions ─────────────────────────────────────────── */}
            {form.questions.map((q, qi) => {
              const hasCorrect = q.options.some(o => o.is_correct);
              return (
                <div
                  key={qi}
                  ref={el => { questionRefs.current[qi] = el; }}
                  className="cct-qcard"
                  onClick={() => setActiveQ(qi)}
                  style={{ background: C.white, border: `1.5px solid ${activeQ === qi ? C.navy : C.border}`, borderRadius: '14px', padding: '22px 24px', boxShadow: activeQ === qi ? '0 2px 12px rgba(14,34,93,0.1)' : '0 1px 4px rgba(14,34,93,0.04)', cursor: 'default', transition: 'border-color 0.15s, box-shadow 0.15s' }}
                >
                  {/* Question header */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px', gap: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ background: C.navy, color: C.white, fontSize: '0.72rem', fontWeight: '700', padding: '3px 11px', borderRadius: '20px' }}>Q {qi + 1}</span>
                      {hasCorrect && (
                        <span style={{ background: C.greenDim, color: '#166534', border: `1px solid #86efac`, fontSize: '0.72rem', fontWeight: '600', padding: '3px 11px', borderRadius: '20px' }}>
                          <i className="pi pi-check" style={{ fontSize: '0.65rem', marginRight: '3px' }} />Answer set
                        </span>
                      )}
                      {Object.keys(errors).some(k => k.startsWith(`q${qi}`)) && (
                        <span style={{ background: C.redDim, color: C.red, border: `1px solid ${C.redBorder}`, fontSize: '0.72rem', fontWeight: '600', padding: '3px 11px', borderRadius: '20px' }}>
                          <i className="pi pi-exclamation-circle" style={{ fontSize: '0.65rem', marginRight: '3px' }} />Needs attention
                        </span>
                      )}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                      {/* Marks */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px', background: C.goldDim, border: `1px solid ${C.goldBorder}`, borderRadius: '8px', padding: '5px 10px' }}>
                        <i className="pi pi-star" style={{ fontSize: '0.7rem', color: '#a06000' }} />
                        <input
                          type="number"
                          min="0.5"
                          step="0.5"
                          value={q.marks}
                          onChange={e => setQuestionMarks(qi, e.target.value)}
                          onClick={e => e.stopPropagation()}
                          style={{ width: '38px', border: 'none', background: 'transparent', fontSize: '0.82rem', fontWeight: '700', color: '#a06000', outline: 'none', textAlign: 'center', fontFamily: 'inherit' }}
                        />
                        <span style={{ fontSize: '0.72rem', color: '#a06000', fontWeight: '600' }}>pts</span>
                      </div>

                      <button
                        className="cct-ghost"
                        title="Duplicate question"
                        onClick={e => { e.stopPropagation(); duplicateQuestion(qi); }}
                        style={{ width: '30px', height: '30px', border: `1.5px solid ${C.border}`, borderRadius: '7px', background: C.white, color: C.sub, fontSize: '0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }}
                      >
                        <i className="pi pi-copy" style={{ fontSize: '0.78rem' }} />
                      </button>

                      {form.questions.length > 1 && (
                        <button
                          className="cct-remove"
                          title="Remove question"
                          onClick={e => { e.stopPropagation(); removeQuestion(qi); }}
                          style={{ width: '30px', height: '30px', border: `1.5px solid ${C.border}`, borderRadius: '7px', background: C.white, color: C.sub, fontSize: '0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }}
                        >
                          <i className="pi pi-trash" style={{ fontSize: '0.78rem' }} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Question text */}
                  <Field label="Question" required error={errors[`q${qi}_text`]} style={{ marginBottom: '16px' }}>
                    <textarea
                      className="cct-opt"
                      style={{ ...inputStyle(!!errors[`q${qi}_text`]), minHeight: '72px', resize: 'vertical' }}
                      placeholder="Enter the question…"
                      value={q.question_text}
                      onChange={e => setQuestionText(qi, e.target.value)}
                      onClick={e => e.stopPropagation()}
                    />
                  </Field>

                  {/* Options */}
                  <div style={{ marginBottom: errors[`q${qi}_correct`] || errors[`q${qi}_opts`] ? '4px' : '0' }}>
                    <div style={{ fontSize: '0.78rem', fontWeight: '700', color: C.navy, marginBottom: '8px', letterSpacing: '0.02em' }}>
                      Answer Options <span style={{ color: C.red }}>*</span>
                      <span style={{ fontSize: '0.7rem', color: C.muted, fontWeight: '400', marginLeft: '6px' }}>Click the circle to mark the correct answer</span>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                      {q.options.map((opt, oi) => (
                        <div
                          key={oi}
                          style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', border: `1.5px solid ${opt.is_correct ? C.goldBorder : errors[`q${qi}_opt${oi}`] ? C.redBorder : C.border}`, borderRadius: '10px', background: opt.is_correct ? C.goldDim : C.white, transition: 'all 0.15s' }}
                        >
                          <input
                            type="radio"
                            className="cct-radio"
                            name={`q${qi}_correct`}
                            checked={opt.is_correct}
                            onChange={() => setOptionCorrect(qi, oi)}
                            onClick={e => e.stopPropagation()}
                            title="Mark as correct answer"
                          />
                          <div style={{ width: '22px', height: '22px', borderRadius: '6px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: '700', background: opt.is_correct ? C.gold : 'transparent', border: opt.is_correct ? `1.5px solid ${C.gold}` : `1.5px solid ${C.border}`, color: opt.is_correct ? C.navy : C.muted, transition: 'all 0.15s' }}>
                            {OPTION_LETTERS[oi]}
                          </div>
                          <input
                            className="cct-opt"
                            style={{ flex: 1, padding: '6px 10px', border: `1.5px solid ${errors[`q${qi}_opt${oi}`] ? C.redBorder : 'transparent'}`, borderRadius: '7px', fontSize: '0.85rem', color: C.body, background: 'transparent', outline: 'none', fontFamily: 'inherit', transition: 'border-color 0.15s' }}
                            placeholder={`Option ${OPTION_LETTERS[oi]}`}
                            value={opt.option_text}
                            onChange={e => setOptionText(qi, oi, e.target.value)}
                            onClick={e => e.stopPropagation()}
                          />
                          {q.options.length > 2 && (
                            <button
                              onClick={e => { e.stopPropagation(); removeOption(qi, oi); }}
                              title="Remove option"
                              style={{ width: '24px', height: '24px', border: 'none', borderRadius: '6px', background: 'transparent', color: C.muted, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.15s' }}
                              onMouseEnter={e => { e.currentTarget.style.color = C.red; e.currentTarget.style.background = C.redDim; }}
                              onMouseLeave={e => { e.currentTarget.style.color = C.muted; e.currentTarget.style.background = 'transparent'; }}
                            >
                              <i className="pi pi-times" style={{ fontSize: '0.7rem' }} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>

                    {errors[`q${qi}_correct`] && (
                      <div style={{ fontSize: '0.72rem', color: C.red, marginTop: '5px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <i className="pi pi-exclamation-circle" style={{ fontSize: '0.7rem' }} /> {errors[`q${qi}_correct`]}
                      </div>
                    )}
                    {errors[`q${qi}_opts`] && (
                      <div style={{ fontSize: '0.72rem', color: C.red, marginTop: '5px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <i className="pi pi-exclamation-circle" style={{ fontSize: '0.7rem' }} /> {errors[`q${qi}_opts`]}
                      </div>
                    )}

                    {q.options.length < 6 && (
                      <button
                        className="cct-ghost"
                        onClick={e => { e.stopPropagation(); addOption(qi); }}
                        style={{ marginTop: '8px', display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 14px', border: `1.5px dashed ${C.border}`, borderRadius: '8px', background: 'transparent', color: C.sub, fontSize: '0.78rem', fontWeight: '600', cursor: 'pointer', transition: 'all 0.15s' }}
                      >
                        <i className="pi pi-plus" style={{ fontSize: '0.72rem' }} /> Add Option
                      </button>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Add Question button */}
            <button
              className="cct-ghost"
              onClick={addQuestion}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '14px', border: `1.5px dashed ${C.border}`, borderRadius: '14px', background: C.white, color: C.sub, fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer', transition: 'all 0.15s', width: '100%' }}
            >
              <i className="pi pi-plus-circle" style={{ fontSize: '0.95rem' }} /> Add Question
            </button>

            <div style={{ height: '16px' }} />
          </div>

          {/* ── SIDEBAR ─────────────────────────────────────────────── */}
          <div style={{ width: '220px', borderLeft: `1px solid ${C.border}`, background: C.white, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px', flexShrink: 0 }}>

            {/* Summary */}
            <div>
              <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em', color: C.muted, marginBottom: '8px' }}>Summary</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '7px' }}>
                {[
                  { num: form.questions.length, lbl: 'Questions' },
                  { num: totalMarks,            lbl: 'Total Marks' },
                  { num: form.questions.filter(q => q.options.some(o => o.is_correct)).length,  lbl: 'Ready' },
                  { num: form.questions.filter(q => !q.options.some(o => o.is_correct)).length, lbl: 'Pending' },
                ].map(({ num, lbl }) => (
                  <div key={lbl} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px 10px' }}>
                    <div style={{ fontSize: '1.2rem', fontWeight: '800', color: C.navy, lineHeight: 1 }}>{num}</div>
                    <div style={{ fontSize: '10px', color: C.muted, marginTop: '3px' }}>{lbl}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Duration pill */}
            <div style={{ padding: '10px 12px', background: C.goldDim, border: `1px solid ${C.goldBorder}`, borderRadius: '10px' }}>
              <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em', color: '#a06000', marginBottom: '4px' }}>Duration</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <i className="pi pi-clock" style={{ fontSize: '0.85rem', color: '#a06000' }} />
                <span style={{ fontSize: '1rem', fontWeight: '800', color: '#a06000' }}>{fmtDuration(form.duration_minutes)}</span>
              </div>
            </div>

            {/* Question palette */}
            <div>
              <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em', color: C.muted, marginBottom: '8px' }}>Question Palette</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '5px' }}>
                {form.questions.map((_, qi) => (
                  <button
                    key={qi}
                    className="cct-qdot"
                    onClick={() => {
                      setActiveQ(qi);
                      questionRefs.current[qi]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    style={{ width: '36px', height: '36px', borderRadius: '8px', border: '1.5px solid', fontSize: '11px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.12s', ...qDotStyle(qi) }}
                  >
                    {qi + 1}
                  </button>
                ))}
              </div>
            </div>

            {/* Legend */}
            <div>
              <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em', color: C.muted, marginBottom: '8px' }}>Legend</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {[
                  { bg: C.navy,     border: C.navy,      label: 'Active' },
                  { bg: C.greenDim, border: '#86efac',   label: 'Answer set' },
                  { bg: C.redDim,   border: C.redBorder, label: 'Has errors' },
                  { bg: C.white,    border: C.border,    label: 'Incomplete' },
                ].map(({ bg, border, label }) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '11px', color: C.sub }}>
                    <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: bg, border: `1.5px solid ${border}`, flexShrink: 0 }} />
                    {label}
                  </div>
                ))}
              </div>
            </div>

            {/* Branch info */}
            {profile && (
              <div style={{ marginTop: 'auto', padding: '10px 12px', background: C.navyHover, border: `1px solid rgba(14,34,93,0.12)`, borderRadius: '10px' }}>
                <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em', color: C.navy, marginBottom: '6px' }}>Assigned to</div>
                <div style={{ fontSize: '0.78rem', fontWeight: '700', color: C.navy, lineHeight: 1.4 }}>{profile.branch_name}</div>
                <div style={{ fontSize: '0.72rem', color: C.sub, marginTop: '2px' }}>{profile.institute_name}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateClassTest;